import { useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import { getSocketOrigin, isChatSocketClientEnabled } from '../config/socketUrl';

export function useChatSocket({ token, conversationId, enabled, onMessage }) {
  const socketRef = useRef(null);
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;

  useEffect(() => {
    if (!isChatSocketClientEnabled() || !enabled || !token || !conversationId) return undefined;

    const socket = io(getSocketOrigin(), {
      path: '/socket.io',
      transports: ['polling', 'websocket'],
      upgrade: true,
      timeout: 10000,
      reconnection: true,
      reconnectionAttempts: 8,
      reconnectionDelay: 1500,
      reconnectionDelayMax: 8000,
      auth: { token },
    });

    socketRef.current = socket;
    socket.on('message:new', (msg) => onMessageRef.current?.(msg));

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [enabled, token, conversationId]);

  const sendMessage = useCallback((body) => {
    return new Promise((resolve) => {
      const s = socketRef.current;
      if (!s) {
        resolve({ ok: false, error: 'Not connected' });
        return;
      }
      let done = false;
      const timeout = setTimeout(() => {
        if (done) return;
        done = true;
        resolve({ ok: false, error: 'Timed out waiting for server' });
      }, 8000);
      s.emit('message:send', { body }, (ack) => {
        if (done) return;
        done = true;
        clearTimeout(timeout);
        resolve(ack || { ok: true });
      });
    });
  }, []);

  return { sendMessage };
}

